//import { getEnumMesaTypeValues    } from '../autogen/bundle.js';
import { getEnumMesaTypeName      } from '../autogen/bundle.js';
import { getEnumMesaStatusName    } from '../autogen/bundle.js';
import { getStrWithLowFirstLetter } from '../util.js';
import { getDurationStr           } from '../util.js';
import { getDurationStrSimple     } from '../util.js';
import { getOptionalTextMomentStr } from '../util.js';
import { isNumberSpecified        } from '../util.js';
import { isRoleAdmin              } from '../util.js';

/**
 * @typedef { import("../auth.js").Role } Role
 */

export const MESA_TYPE_NONE                  = 0;
export const MESA_TYPE_FOSTER_KID_REPEATED   = 1;
export const MESA_TYPE_NESTLING_REPEATED     = 2;
export const MESA_TYPE_JIGSAW_REPEATED       = 3;
export const MESA_TYPE_SCULPTURE_REPEATED    = 4;
export const MESA_TYPE_CAKE21_REPEATED       = 5;

export const MESA_TYPE_VALUE_FOSTER_KID_REPEATED   = 198365924;
export const MESA_TYPE_VALUE_NESTLING_REPEATED     = 151361348;
export const MESA_TYPE_VALUE_JIGSAW_REPEATED       = 23441217;
export const MESA_TYPE_VALUE_SCULPTURE_REPEATED    = 60952756;
export const MESA_TYPE_VALUE_CAKE21_REPEATED       = 166692576;

// options for component b-form-select
export function createMesaTypeOptions(mesaProtoIds) {
  const options = [];

  for(const mesaProtoId of mesaProtoIds) {
    const option = {
      value: mesaProtoId,
      text: getMesaTypeName(mesaProtoId),
    };
    options.push(option);
  }

  return options;
}

/**
 * @typedef { import('./event.js').PlayerGroupId } PlayerGroupId
 */

/**
 * @typedef {number} MesaStatus
 *
 * @typedef {Object} Mesa
 * @property {number} id
 * @property {number} protoId
 * @property {MesaStatus} status
 * @property {number} ctime
 * @property {number} stime
 * @property {number} srtime
 * @property {number} ftime
 * @property {number} frtime
 * @property {number} clientMinVersionCode
 * @property {PlayerGroupId} playerGroupId
 */

export const MESA_STARTING_TYPE_IMMEDIATELY = 1;
export const MESA_STARTING_TYPE_DEFFERED    = 2;

/**
 * @param {number} code
 * @returns {string}
 */
export function getMesaStartingTypeName(code) {
  switch(code) {
    case MESA_STARTING_TYPE_IMMEDIATELY: return 'immediately';
    case MESA_STARTING_TYPE_DEFFERED:    return 'deffered';
  }
  return `Unknown mesa starting type ${code}.`;
}

export const MESA_STATUS_INACTIVE = 0;
export const MESA_STATUS_ACTIVE   = 1;
export const MESA_STATUS_CLOSED   = 2;

/**
 * @param {MesaStatus} status
 * @returns {boolean}
 */
export function isMesaIncoming(status) {
  return status === MESA_STATUS_INACTIVE;
}

/**
 * @param {MesaStatus} status
 * @returns {boolean}
 */
export function isMesaActive(status) {
  return status === MESA_STATUS_ACTIVE;
}

/**
 * @param {MesaStatus} status
 * @returns {boolean}
 */
export function isMesaClosed(status) {
  return status === MESA_STATUS_CLOSED;
}

//-----------------------------------------------------------------------------

/**
 * @param {MesaStatus} status
 * @returns {boolean}
 */
export function isMesaEditable(status) {
  return isMesaStimeEditable(status) || isMesaFtimeEditable(status);
}

/**
 * @param {MesaStatus} status
 * @returns {boolean}
 */
export function isMesaStimeEditable(status) {
  return isMesaIncoming(status);
}

/**
 * @param {MesaStatus} status
 * @returns {boolean}
 */
export function isMesaFtimeEditable(status) {
  return isMesaIncoming(status) || isMesaActive(status);
}

//-----------------------------------------------------------------------------

/**
 * @param {number} typeCode
 * @returns {string}
 */
export function getMesaTypeName(typeCode) {
  return getEnumMesaTypeNameFormatted(typeCode);
}

/**
 * @param {number} typeCode
 * @returns {string}
 */
export function getEnumMesaTypeNameFormatted(typeCode) {
  const name = getEnumMesaTypeName(typeCode);
  return getStrWithLowFirstLetter(name);
}

/**
 * @param {number} typeCode
 * @returns {string}
 */
export function getEnumMesaStatusNameFormatted(typeCode) {
  const name = getEnumMesaStatusName(typeCode);
  return getStrWithLowFirstLetter(name);
}

/**
 * @param {MesaStatus} status
 * @returns {Object}
 */
export function getMesaStatusClass(status) {
  return {
    activeMesa: isMesaActive(status),
  };
}

/**
 * @param {Mesa} mesa
 * @returns {number}
 */
export function getMesaPlannedDuration(mesa) {
  return mesa.ftime - mesa.stime;
}

/**
 * @param {Mesa} mesa
 * @returns {string}
 */
export function getMesaPlannedDurationStr(mesa) {
  const plannedDuration = getMesaPlannedDuration(mesa);
  return getDurationStr(plannedDuration);
}

/**
 * @param {Mesa} mesa
 * @returns {number}
 */
export function getRealDurationMesa(mesa) {
  if(mesa.type === MESA_TYPE_FOSTER_KID_REPEATED) {
    return 24*60*60;
  }
  return 48*60*60;
}

/**
 * @param {Mesa} mesa
 * @returns {number}
 */
export function getEndTimeLastMesa(mesa) {
  const realDuration = getRealDurationMesa(mesa);
  return mesa.ftime + realDuration;
}

/**
 * @param {Mesa} mesa
 * @returns {string}
 */
export function getEndTimeLastMesaStr(mesa) {
  const endTime = getEndTimeLastMesa(mesa);
  return getDurationStr(endTime);
}

/**
 * @param {Mesa} mesa
 * @param {number} nowSec
 * @returns {string}
 */
export function getMesaRemainingTimeStr(mesa, nowSec) {
  if(mesa.status === MESA_STATUS_INACTIVE) {
    return 'incoming';
  }

  if(mesa.status === MESA_STATUS_CLOSED) {
    return 'finished';
  }

  const timeEnded = nowSec >= mesa.ftime;
  if(timeEnded) {
    return 'time ended';
  }

  const remainintTime = mesa.ftime - nowSec;
  return getDurationStrSimple(remainintTime);
}

/**
 * @param {number} frtime
 * @returns {string}
 */
export function getMesaStartRealTimeStr(frtime) {
  return getOptionalTextMomentStr(frtime, 'not started');
}

/**
 * @param {number} frtime
 * @returns {string}
 */
export function getMesaFinishRealTimeStr(frtime) {
  return getOptionalTextMomentStr(frtime, 'not finished');
}

/**
 * @param {Mesa} mesa
 * @returns {string}
 */
export function getMesaRealDurationStrSimple(mesa) {
  if(!isNumberSpecified(mesa.srtime)) {
    return 'not started';
  }
  if(!isNumberSpecified(mesa.frtime)) {
    return 'not finished';
  }
  const realDuration = mesa.frtime - mesa.srtime;
  return getDurationStrSimple(realDuration);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayManageMesas(role) {
  return isRoleAdmin(role);
}

//-----------------------------------------------------------------------------
// Состояние месы у игрока
//-----------------------------------------------------------------------------

/**
 * @typedef {number} PlayerMesaStatus
 */

// EnumMesaState
export const MESA_STATE_INACTIVE          = 0;
export const MESA_STATE_ACTIVE            = 1;
export const MESA_STATE_FINISHED_COMPLETE = 2;
export const MESA_STATE_FINISHED_IGNORED  = 3;

export function getMesaStateName(stateCode) {
  switch(stateCode) {
    case MESA_STATE_INACTIVE:          return 'inactive';
    case MESA_STATE_ACTIVE:            return 'active';
    case MESA_STATE_FINISHED_COMPLETE: return 'finished complete';
    case MESA_STATE_FINISHED_IGNORED:  return 'finished ignored';

    default:
      return `Unknown state code (${stateCode})`;
  }
}

/**
 * @param {PlayerMesaStatus} status
 * @returns {Object}
 */
export function getPlayerMesaStatusClass(state) {
  if(isPlayerMesaActive(state)) {
    return {
      activePlayerMesa: true,
    };
  }

  return null;
}

export function getFromInsiderStr(fromInsiderCode) {
  if(fromInsiderCode === 0) {
    return 'нет';
  }
  return 'да';
}

/**
 * Функция возвращает true, если меса активна у игрока.
 * 
 * @param {PlayerMesaStatus} status
 * @returns {boolean}
 */
export function isPlayerMesaActive(state) {
  return state === MESA_STATE_ACTIVE;
}

/**
 * Функция возвращает true, если у игрока есть хотя бы одна активная меса.
 *
 * @param {Object[]} mesas
 * @returns {boolean}
 */
export function hasActivePlayerMesas(mesas) {
  for(const mesa of mesas) {
    if(isPlayerMesaActive(mesa.state)) {
      return true;
    }
  }

  return false;
}
